import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  IconButton,
} from "@material-ui/core";
import PollIcon from "@material-ui/icons/Poll";
import RefreshIcon from "@material-ui/icons/Refresh";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { useDate } from "../../hooks/useDate";

const t = (key) => i18n.t(`campaigns.dialog.poll.${key}`);

const CampaignPollResults = ({ campaignId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { datetimeToClient } = useDate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/campaigns/${campaignId}/poll-results`);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    load();
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [load]);

  if (!data?.enabled) return null;
  const maxVotes = Math.max(1, ...data.options.map((o) => o.votes));

  return (
    <Paper variant="outlined" style={{ padding: 16, marginTop: 16, borderRadius: 12 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <PollIcon color="primary" />
          <Typography variant="h6">{data.mode && data.mode !== "poll" ? "Respuestas a las opciones" : t("results")}</Typography>
        </Box>
        <IconButton size="small" onClick={load} disabled={loading}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>
      <Typography variant="subtitle1" style={{ fontWeight: 600, marginTop: 8 }}>
        {data.question}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {t("sent")}: {data.sent} · {t("voters")}: {data.totalVoters}
        {data.sent > 0 ? ` (${Math.round((data.totalVoters / data.sent) * 100)}%)` : ""}
      </Typography>

      <Box mt={2}>
        {data.options.map((opt) => {
          const pct = data.totalVoters ? Math.round((opt.votes / data.totalVoters) * 100) : 0;
          return (
            <Box key={opt.name} mb={1.5}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">{opt.name}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {opt.votes} {t("votes")} · {pct}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(opt.votes / maxVotes) * 100}
                style={{ height: 8, borderRadius: 4, marginTop: 4 }}
              />
            </Box>
          );
        })}
      </Box>

      <Typography variant="subtitle2" style={{ marginTop: 16 }}>
        {t("voterList")}
      </Typography>
      {data.voters.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          {t("noVotes")}
        </Typography>
      ) : (
        <Box style={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t("contacts.table.name", { defaultValue: "Nombre" })}</TableCell>
                <TableCell>{i18n.t("contacts.table.whatsapp", { defaultValue: "Número" })}</TableCell>
                <TableCell>{t("option")}</TableCell>
                <TableCell>{i18n.t("campaigns.report.scheduling", { defaultValue: "Fecha" })}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.voters.map((v) => (
                <TableRow key={v.number}>
                  <TableCell>{v.name || "-"}</TableCell>
                  <TableCell>{v.number}</TableCell>
                  <TableCell>{v.selected.join(", ")}</TableCell>
                  <TableCell>{v.votedAt ? datetimeToClient(v.votedAt) : "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
};

export default CampaignPollResults;
